'use client';

import { useEffect } from 'react';
import { clientLogger } from '@/lib/client-logger';
const log = clientLogger.child('pwa');

export function ServiceWorkerRegistration() {
  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      // Only register if explicitly enabled or in development
      const shouldRegister = process.env.NEXT_PUBLIC_ENABLE_PWA === 'true';
      
      if (!shouldRegister) {
        return;
      }

      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/sw.js')
          .then((registration) => {
            // Check for updates more frequently for faster updates
            setInterval(() => {
              registration.update();
            }, 15 * 60 * 1000); // Check every 15 minutes

            // Handle updates
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // New service worker available - show update notification
                    showUpdateNotification();
                  }
                });
              }
            });
            
            // Also check immediately on registration
            registration.update();
          })
          .catch((error) => {
            log.error('Service Worker registration failed', { err: error });
          });
      });

      // Handle offline/online status
      window.addEventListener('online', () => {
        // Trigger any pending syncs
        if (navigator.serviceWorker.controller && 'sync' in navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'SYNC_DATA'
          });
        }
      });

      window.addEventListener('offline', () => {
        // Working offline - data will sync when connection returns
      });

      // Clean up on unmount
      return () => {
        // Service worker persists, no cleanup needed
      };
    }
  }, []);

  return null;
}

/**
 * Show a modern update notification instead of intrusive confirm dialog
 */
function showUpdateNotification() {
  // Create update banner
  const banner = document.createElement('div');
  banner.id = 'pwa-update-banner';
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
    color: white;
    padding: 16px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: system-ui, -apple-system, sans-serif;
    animation: slideDown 0.3s ease-out;
  `;
  
  banner.innerHTML = `
    <style>
      @keyframes slideDown {
        from { transform: translateY(-100%); }
        to { transform: translateY(0); }
      }
      @keyframes slideUp {
        from { transform: translateY(0); }
        to { transform: translateY(-100%); }
      }
      #pwa-update-banner.hide {
        animation: slideUp 0.3s ease-in forwards;
      }
    </style>
    <div style="display: flex; align-items: center; gap: 12px;">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        <path d="M12 8v4m0 4h.01"/>
      </svg>
      <span style="font-weight: 500;">A new version is available!</span>
    </div>
    <div style="display: flex; gap: 8px;">
      <button id="pwa-update-btn" style="
        background: white;
        color: #0891b2;
        border: none;
        padding: 8px 16px;
        border-radius: 6px;
        font-weight: 600;
        cursor: pointer;
        font-size: 14px;
      ">Update Now</button>
      <button id="pwa-dismiss-btn" style="
        background: transparent;
        color: white;
        border: 1px solid rgba(255,255,255,0.3);
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
      ">Later</button>
    </div>
  `;
  
  document.body.appendChild(banner);
  
  // Update button - reload immediately
  document.getElementById('pwa-update-btn')?.addEventListener('click', () => {
    window.location.reload();
  });
  
  // Dismiss button - hide but remind later
  document.getElementById('pwa-dismiss-btn')?.addEventListener('click', () => {
    banner.classList.add('hide');
    setTimeout(() => banner.remove(), 300);
    
    // Remind in 30 minutes
    setTimeout(() => {
      if (!document.getElementById('pwa-update-banner')) {
        showUpdateNotification();
      }
    }, 30 * 60 * 1000);
  });
}
